import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  registerOrgSchema,
  requestInviteSchema,
  type RegisterOrgInput,
  type RequestInviteInput,
} from '@cmms/validation'
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useNavigate } from 'react-router-dom'
import { AuthShell } from '../../layouts/AuthShell'
import { api } from '../../lib/api'
import { APP_NAME } from '../../lib/branding'
import { useAuthStore } from '../../store/authStore'
import { ApiError } from '@cmms/api-client'

type RegStep = 'email' | 'pending' | 'details'

function slugify(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

export function RegisterOrgPage() {
  const navigate = useNavigate()
  const setTokens = useAuthStore((s) => s.setTokens)
  const setUser = useAuthStore((s) => s.setUser)

  const [step, setStep] = useState<RegStep>('email')
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const stepIndex = step === 'email' ? 0 : step === 'pending' ? 0 : 1

  const subtitle =
    step === 'email'
      ? `Submit a request and a ${APP_NAME} admin will email you an invite code once approved.`
      : step === 'pending'
        ? `We've queued the request from ${email}. Check your inbox once an admin approves it.`
        : `Paste the invite code from your email and finish setting up your workspace.`

  return (
    <AuthShell
      title={
        step === 'email' ? 'Request access'
        : step === 'pending' ? 'Request received'
        : 'Almost there'
      }
      subtitle={subtitle}
      swap={{ prompt: 'Already have an account?', cta: 'Log In', to: '/login' }}
    >
      {/* Step indicator */}
      <Stepper activeStep={stepIndex} alternativeLabel sx={{ mb: 4 }}>
        <Step>
          <StepLabel>Request invite</StepLabel>
        </Step>
        <Step>
          <StepLabel>Account details</StepLabel>
        </Step>
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      {step === 'email' && (
        <EmailStep
          onSubmitted={(addr) => {
            setEmail(addr)
            setError(null)
            setStep('pending')
          }}
          onIHaveCode={() => {
            setError(null)
            setStep('details')
          }}
          onError={setError}
        />
      )}

      {step === 'pending' && (
        <PendingStep
          email={email}
          onIHaveCode={() => {
            setError(null)
            setStep('details')
          }}
          onChangeEmail={() => {
            setError(null)
            setStep('email')
          }}
        />
      )}

      {step === 'details' && (
        <DetailsStep
          email={email}
          showPassword={showPassword}
          togglePassword={() => setShowPassword((v) => !v)}
          onBack={() => {
            setError(null)
            setStep(email ? 'pending' : 'email')
          }}
          onSubmitted={async (tokens) => {
            setTokens(tokens.access_token, tokens.refresh_token)
            const user = await api.auth.me()
            setUser(user)
            navigate('/dashboard', { replace: true })
          }}
          onError={setError}
        />
      )}
    </AuthShell>
  )
}

// ─── Step 1: Email ────────────────────────────────────────────────────────────

function EmailStep({
  onSubmitted,
  onIHaveCode,
  onError,
}: {
  onSubmitted: (email: string) => void
  onIHaveCode: () => void
  onError: (msg: string) => void
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RequestInviteInput>({
    resolver: zodResolver(requestInviteSchema),
    defaultValues: { email: '' },
  })

  // Live cooldown when the backend returns 429. We tick down once a
  // second so the button label shows e.g. "Try again in 4m 12s".
  const [cooldown, setCooldown] = useState<{ secs: number; reason: string } | null>(null)
  const tickRef = useRef<number | null>(null)
  useEffect(() => {
    if (!cooldown) return
    tickRef.current = window.setInterval(() => {
      setCooldown((c) => {
        if (!c) return null
        const next = c.secs - 1
        return next <= 0 ? null : { ...c, secs: next }
      })
    }, 1000)
    return () => {
      if (tickRef.current != null) window.clearInterval(tickRef.current)
    }
  }, [cooldown !== null])

  function fmt(s: number) {
    if (s <= 0) return '0s'
    const m = Math.floor(s / 60)
    const r = s % 60
    if (m === 0) return `${r}s`
    if (m < 60) return r === 0 ? `${m}m` : `${m}m ${r}s`
    const h = Math.floor(m / 60)
    const mm = m % 60
    return `${h}h ${mm}m`
  }

  async function onSubmit(values: RequestInviteInput) {
    if (cooldown && cooldown.secs > 0) return
    try {
      const res = await api.auth.requestInvite(values)
      const retry = (res as { retry_after_seconds?: number | null })?.retry_after_seconds ?? 0
      if (retry > 0) setCooldown({ secs: retry, reason: 'cooldown' })
      onSubmitted(values.email)
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const d = err.details as
          | { retry_after_seconds?: number; reason?: string }
          | undefined
        const secs = d?.retry_after_seconds ?? 60
        setCooldown({ secs, reason: d?.reason ?? 'cooldown' })
        onError(err.message)
        return
      }
      onError(err instanceof ApiError ? err.message : 'Could not send request')
    }
  }

  const cooling = (cooldown?.secs ?? 0) > 0
  const buttonLabel = isSubmitting
    ? 'Submitting…'
    : cooling
      ? `Try again in ${fmt(cooldown!.secs)}`
      : 'Submit request'

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2.5}>
        <Alert severity="info" sx={{ borderRadius: 2, fontSize: 13 }}>
          New {APP_NAME} workspaces are reviewed by an admin before an invite
          code is issued. Approval typically arrives within one business day.
        </Alert>

        {cooling && (
          <Alert severity="warning" icon={false} sx={{ borderRadius: 2, fontSize: 13 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 0.25 }}>
              {cooldown!.reason === 'active_code'
                ? 'You already have an active invite code'
                : 'You just submitted a request'}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              {cooldown!.reason === 'active_code'
                ? `Check your email — the code is still valid. You can request a new one in ${fmt(cooldown!.secs)}.`
                : `Please wait ${fmt(cooldown!.secs)} before submitting again.`}
            </Typography>
          </Alert>
        )}

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
            Email address
          </Typography>
          <TextField
            fullWidth
            type="email"
            placeholder="john@example.com"
            autoComplete="email"
            error={!!errors.email}
            helperText={errors.email?.message ?? "We'll email your invite code here once approved."}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              },
            }}
            {...register('email')}
          />
        </Box>

        <Box sx={{ pt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={isSubmitting || cooling}
            sx={{ borderRadius: 999, py: 1.5, fontWeight: 600 }}
          >
            {buttonLabel}
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', pt: 0.5 }}>
          <Link
            component="button"
            type="button"
            onClick={onIHaveCode}
            sx={{ fontSize: 13, fontWeight: 600 }}
          >
            I already have an invite code →
          </Link>
        </Box>
      </Stack>
    </Box>
  )
}

// ─── Step 1.5: Pending review ────────────────────────────────────────────────

function PendingStep({
  email,
  onIHaveCode,
  onChangeEmail,
}: {
  email: string
  onIHaveCode: () => void
  onChangeEmail: () => void
}) {
  return (
    <Stack spacing={2.5}>
      <Alert severity="success" icon={<MarkEmailReadOutlinedIcon />}
        sx={{ borderRadius: 2 }}>
        We've queued your request. <strong>{email}</strong> will get an email with
        an invite code as soon as a {APP_NAME} admin approves it. You can close this
        window and come back later.
      </Alert>
      <Stack direction="row" spacing={1.5}>
        <Button
          onClick={onIHaveCode}
          variant="contained"
          size="large"
          fullWidth
          sx={{ borderRadius: 999, py: 1.5, fontWeight: 600 }}
        >
          I have a code
        </Button>
        <Button
          onClick={onChangeEmail}
          variant="outlined"
          size="large"
          sx={{ borderRadius: 999, py: 1.5, fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          Change email
        </Button>
      </Stack>
    </Stack>
  )
}

// ─── Step 2: Details ──────────────────────────────────────────────────────────

function DetailsStep({
  email,
  showPassword,
  togglePassword,
  onBack,
  onSubmitted,
  onError,
}: {
  email: string
  showPassword: boolean
  togglePassword: () => void
  onBack: () => void
  onSubmitted: (tokens: { access_token: string; refresh_token: string }) => Promise<void>
  onError: (msg: string) => void
}) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterOrgInput>({
    resolver: zodResolver(registerOrgSchema),
    defaultValues: {
      invite_code: '',
      org_name: '',
      org_slug: '',
      admin_email: email,
      admin_password: '',
      admin_full_name: '',
      admin_phone: '',
    },
  })

  // Auto-fill state — true once the invite-code lookup populated the
  // email field, so we can show a small "verified" hint.
  const [emailAutoFilled, setEmailAutoFilled] = useState(false)
  const lastLookupRef = useRef<string>('')
  const watchedCode = watch('invite_code')
  const watchedEmail = watch('admin_email')

  useEffect(() => {
    const clean = (watchedCode ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (clean.length !== 8) return
    if (clean === lastLookupRef.current) return
    lastLookupRef.current = clean
    const formatted = `${clean.slice(0, 4)}-${clean.slice(4)}`
    let cancelled = false
    api.auth.lookupInviteCode(formatted)
      .then((res) => {
        if (cancelled) return
        if (!res.valid || !res.email) return
        // Only auto-fill if the field is empty or was previously
        // auto-filled — never stomp on a user-typed email.
        const current = (watchedEmail ?? '').trim()
        if (current === '' || emailAutoFilled) {
          setValue('admin_email', res.email, { shouldValidate: true })
          setEmailAutoFilled(true)
        }
      })
      .catch(() => {/* lookup is best-effort; submit will validate */})
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedCode])

  /** Normalise the invite-code field to ALL-CAPS XXXX-XXXX as the user types. */
  function formatInviteCode(raw: string) {
    const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
    return clean.length <= 4 ? clean : `${clean.slice(0, 4)}-${clean.slice(4)}`
  }

  async function onSubmit(values: RegisterOrgInput) {
    try {
      const cleaned = { ...values, admin_phone: values.admin_phone || undefined }
      const tokens = await api.auth.registerOrg(cleaned)
      await onSubmitted(tokens)
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Registration failed')
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2.5}>
        {/* Code first — it's what they just received via email */}
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
            Invite code
          </Typography>
          <TextField
            fullWidth
            placeholder="XXXX-XXXX"
            autoComplete="one-time-code"
            value={watchedCode ?? ''}
            onChange={(e) => setValue('invite_code', formatInviteCode(e.target.value),
              { shouldValidate: true })}
            error={!!errors.invite_code}
            helperText={
              errors.invite_code?.message ?? (
                watchedEmail
                  ? emailAutoFilled
                    ? `Verified — code matches ${watchedEmail}`
                    : `Will sign up as ${watchedEmail}`
                  : undefined
              )
            }
            FormHelperTextProps={{
              sx: emailAutoFilled
                ? { color: 'success.main', fontWeight: 600 }
                : undefined,
            }}
            slotProps={{
              htmlInput: {
                style: { fontFamily: 'ui-monospace, monospace', letterSpacing: 4 },
                inputMode: 'text',
                autoCapitalize: 'characters',
                spellCheck: false,
              },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Divider textAlign="left" sx={{ color: 'text.secondary', fontSize: 12 }}>
          About you
        </Divider>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
            Full name
          </Typography>
          <TextField
            fullWidth
            placeholder="John Kyte"
            autoComplete="name"
            error={!!errors.admin_full_name}
            helperText={errors.admin_full_name?.message}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              },
            }}
            {...register('admin_full_name')}
          />
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
              Email address
            </Typography>
            <TextField
              fullWidth
              value={email}
              disabled
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
              }}
              {...register('admin_email')}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
              Phone number{' '}
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                (optional)
              </Box>
            </Typography>
            <TextField
              fullWidth
              type="tel"
              placeholder="+1 555 123 4567"
              autoComplete="tel"
              error={!!errors.admin_phone}
              helperText={errors.admin_phone?.message}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
              }}
              {...register('admin_phone')}
            />
          </Box>
        </Stack>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
            Password
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            autoComplete="new-password"
            error={!!errors.admin_password}
            helperText={errors.admin_password?.message}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={togglePassword}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            {...register('admin_password')}
          />
        </Box>

        <Divider textAlign="left" sx={{ color: 'text.secondary', fontSize: 12 }}>
          Your organization
        </Divider>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
              Organization name
            </Typography>
            <TextField
              fullWidth
              placeholder="Your company"
              error={!!errors.org_name}
              helperText={errors.org_name?.message}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
              }}
              {...register('org_name', {
                onChange: (e) => setValue('org_slug', slugify(e.target.value)),
              })}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
              Workspace URL
            </Typography>
            <TextField
              fullWidth
              placeholder="your-org"
              error={!!errors.org_slug}
              helperText={
                errors.org_slug?.message ??
                "How your team signs in. Lowercase letters, numbers and hyphens."
              }
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'ui-monospace, monospace',
                          fontSize: 14,
                          color: 'text.secondary',
                          fontWeight: 500,
                        }}
                      >
                        cmms.io/
                      </Box>
                    </InputAdornment>
                  ),
                },
              }}
              {...register('org_slug')}
            />
          </Box>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ pt: 1 }}
        >
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ fontWeight: 600 }}>
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            sx={{ borderRadius: 999, px: 4, minWidth: 160 }}
          >
            {isSubmitting ? 'Creating...' : 'Create account'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
